import React from 'react'
import { useMutation, type InfiniteData } from '@tanstack/react-query'
import { queryClient } from '@core/Data/ReactQuery'
import { useOutsideClick } from 'rooks'
import { Button } from '@mobi/component-library/Common/V2/Buttons'
import { Icon } from '@mobi/component-library/Common/V2/Icon'
import { Tooltip } from '@mobi/component-library/Common/V2/Tooltip'
import { ContextMenu } from '@mobi/component-library/Overlays/ContextMenu'
import { triggerHapticFeedback } from '@core/Utils/hapticFeedback/hapticFeedback'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { colors } from '@mobi/component-library/Theme/Common'
import { useAuthenticatedAccount } from '@core/Components/RequireAuth/hooks'
import {
  postFollowMeFollowAccount,
  postUnFollowMeFollowAccount,
  postFollowedAccountNotifications,
} from '@core/Areas/FollowMe/helpers/api'
import { followMePrincipleProfileQuery } from '@core/Areas/FollowMe/helpers/queries'
import {
  trackFollowMeNotificationToggle,
  trackFollowMeToggleFollow,
} from '@core/Areas/FollowMe/helpers/analytics'
import { handleApiErrorNotification } from '@core/Areas/FollowMe/helpers/handleApiErrorNotification'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import type { FollowMeSharedBet, FollowMeProfile } from '@core/Areas/FollowMe/types'
import {
  getPushNotificationAlertStatus,
  togglePushNotificationAlertStatus,
} from '@core/Utils/notifications/pushNotificationAlerts'
import { FollowButtonLocation } from './types'
import { UnfollowButtonComponent } from './Components/UnfollowButtonComponent'
import { NotificationButtonComponent } from './Components/NotificationButtonComponent'
import { FollowButtonComponent } from './Components/FollowButtonComponent'
import { isReactNativeApp } from '@mobi/web-native-comms/web'

export const FollowButton: React.FC<{
  accountId: string
  accountDisplayName: string
  isFollowing: boolean
  isNotificationsEnabled: boolean
  location: FollowButtonLocation
}> = ({ accountId, accountDisplayName, isFollowing, isNotificationsEnabled, location }) => {
  const [isFollowingState, setIsFollowing] = React.useState(isFollowing)

  const [isNotificationsEnabledState, setIsNotificationsEnabled] =
    React.useState(isNotificationsEnabled)
  React.useEffect(() => setIsFollowing(isFollowing), [isFollowing])
  React.useEffect(() => setIsNotificationsEnabled(isNotificationsEnabled), [isNotificationsEnabled])

  const { accountNumber } = useAuthenticatedAccount()

  const [shouldShowAlertsTooltip, setShouldShowAlertsTooltip] = React.useState<boolean>(false)
  const alertsTooltipRef = React.useRef<HTMLDivElement>(null)
  useOutsideClick(alertsTooltipRef, () => {
    if (shouldShowAlertsTooltip) setShouldShowAlertsTooltip(false)
  })

  const followMutation = useMutation({
    mutationFn: () =>
      isFollowingState
        ? postUnFollowMeFollowAccount(accountId)
        : postFollowMeFollowAccount(accountId),
    onSuccess() {
      const newState = !isFollowingState
      setIsFollowing(newState)
      triggerHapticFeedback('impact-light')
      patchFeedDataWithIsFollowingChange({ accountId, isFollowing: newState })
      updatePrincipalFollowingCount(newState)
      trackFollowMeToggleFollow(newState ? 'enable' : 'disable')

      if (newState) {
        setShouldShowAlertsTooltip(true)
        addToast({
          id: 'bet-loop-follow-success',
          isDismissable: true,
          title: `You started following ${accountDisplayName}!`,
          icon: 'success',
        })
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.followMeAccountProfileBase })
    },
    onError(e) {
      handleApiErrorNotification(e)
    },
  })

  const notifyMutation = useMutation({
    mutationFn: () =>
      checkPushNotificationRegistration(accountId, isNotificationsEnabledState, accountNumber),
    onSuccess() {
      const newNotifState = !isNotificationsEnabledState
      setIsNotificationsEnabled(newNotifState)
      triggerHapticFeedback('impact-light')
      trackFollowMeNotificationToggle(!isNotificationsEnabledState ? 'enable' : 'disable')
      patchFeedDataWithNotificationChange({ accountId, isNotificationsEnabled: newNotifState })

      queryClient.invalidateQueries({ queryKey: queryKeys.followMeAccountProfileBase })
    },
    onError(e) {
      handleApiErrorNotification(e)
    },
  })

  const handleFollowClick = () => {
    followMutation.mutate()
  }

  const handleNotifyClick = () => {
    notifyMutation.mutate()
  }
  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false)
  const contextMenuRef = React.useRef<HTMLButtonElement>(null)

  useOutsideClick(alertsTooltipRef, () => {
    if (contextMenuRef) setIsMenuOpen(false)
  })

  if (!isFollowingState) {
    return (
      <FollowButtonComponent
        isPending={followMutation.isPending}
        handleFollowClick={handleFollowClick}
        location={location}
      />
    )
  }

  return location === 'shared-bet-card' ? (
    <div ref={alertsTooltipRef}>
      <Button
        ref={contextMenuRef}
        color='secondary_color'
        size='sm'
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        <Icon name='DuocolorDotsHorizontal' color={colors.studio[600]} size='2rem' />
      </Button>

      <ContextMenu
        isOpen={isMenuOpen}
        referenceElRef={contextMenuRef}
        offsetYPx={5}
        offsetXPx={-55}
      >
        {isReactNativeApp || process.env.NODE_ENV === 'development' ? (
          <>
            <Button
              size='sm'
              color='secondary_outline'
              disabled={notifyMutation.isPending}
              onClick={() => {
                handleNotifyClick()
                setIsMenuOpen(false)
              }}
            >
              <div>
                {isNotificationsEnabledState ? (
                  <Icon color={colors.studio[500]} name='SolidBellRinging01' />
                ) : (
                  <Icon name='LineBellPlus' />
                )}
                <span>Notifications</span>
              </div>
            </Button>
            {shouldShowAlertsTooltip && !isNotificationsEnabledState && (
              <Tooltip
                color='blue'
                isHidden={notifyMutation.isPending}
                shouldHideCrossIcon
                triggerElRef={alertsTooltipRef}
                hasTooltipBeenSeen={() => false}
                tooltipId='bet-loop-turn-alerts-on'
                onDismissed={() => setShouldShowAlertsTooltip(false)}
                shouldOnlyRenderSingleInstance={true}
                offsetYPx={13}
                offsetXPx={-35}
                flipElement={true}
              >
                <div>Get notified for</div>
                <div>every new bet</div>
              </Tooltip>
            )}
          </>
        ) : null}

        <Button
          size='sm'
          color='secondary_outline'
          disabled={followMutation.isPending}
          onClick={() => {
            handleFollowClick()
            setIsMenuOpen(false)
          }}
        >
          <div>
            <Icon name='DuocolorUsersX' />
            <span>Unfollow</span>
          </div>
        </Button>
      </ContextMenu>
    </div>
  ) : (
    <>
      <UnfollowButtonComponent
        isPending={followMutation.isPending}
        handleFollowClick={handleFollowClick}
        location={location}
      />

      <NotificationButtonComponent
        isPending={notifyMutation.isPending}
        handleNotifyClick={handleNotifyClick}
        isNotificationsEnabledState={isNotificationsEnabledState}
        shouldShowAlertsTooltip={shouldShowAlertsTooltip}
        setShouldShowAlertsTooltip={setShouldShowAlertsTooltip}
        location={location}
      />
    </>
  )
}

// =============
// Local Helpers
// =============

function patchFeeds(
  updater: (
    old?: InfiniteData<FollowMeSharedBet[]>
  ) => InfiniteData<FollowMeSharedBet[]> | undefined
) {
  queryClient.setQueriesData<InfiniteData<FollowMeSharedBet[]>>(
    { queryKey: queryKeys.followMeSharedBetsFeedBase() },
    updater
  )
}

function patchFeedDataWithIsFollowingChange({
  accountId,
  isFollowing,
}: {
  accountId: string
  isFollowing: boolean
}) {
  patchFeeds(old =>
    old
      ? {
          ...old,
          pages: old.pages.map(page =>
            page.map(bet => {
              let ownerAccountFollowerCount = bet.ownerAccountFollowerCount
              return bet.ownerAccountId === accountId
                ? {
                    ...bet,
                    isFollowing,
                    isNotificationsEnabled: false,
                    ownerAccountFollowerCount: isFollowing
                      ? ++ownerAccountFollowerCount
                      : --ownerAccountFollowerCount,
                  }
                : bet
            })
          ),
        }
      : old
  )
}

function patchFeedDataWithNotificationChange({
  accountId,
  isNotificationsEnabled,
}: {
  accountId: string
  isNotificationsEnabled: boolean
}) {
  patchFeeds(old =>
    old
      ? {
          ...old,
          pages: old.pages.map(page =>
            page.map(bet =>
              bet.ownerAccountId === accountId ? { ...bet, isNotificationsEnabled } : bet
            )
          ),
        }
      : old
  )
}

function updatePrincipalFollowingCount(isNowFollowing: boolean) {
  const delta = isNowFollowing ? 1 : -1
  queryClient.setQueryData<FollowMeProfile>(followMePrincipleProfileQuery.queryKey, old =>
    old ? { ...old, followingCount: Math.max(0, old.followingCount + delta) } : old
  )
}

async function checkPushNotificationRegistration(
  accountId: string,
  isNotificationsEnabled: boolean,
  accountNumber: number
) {
  const registered = await getPushNotificationAlertStatus(accountNumber, 'followme')

  if (!registered) {
    await togglePushNotificationAlertStatus({
      accountNumber,
      isEnabled: true,
      alertType: 'followme',
      deepLink: '',
    })
  }
  postFollowedAccountNotifications(accountId, !isNotificationsEnabled)
}
